import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ChevronDown,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  User,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getPageMeta } from "../../config/dashboardNav";
import { performLogout } from "../../utils/authSession";
import { getOpsUserDisplayName } from "../../utils/normalizeOpsUser";
import { showInfo } from "../../utils/toast";
import GlobalSearch from "./GlobalSearch";
import NavTasks from "./NavTasks";

function UserMenu({ onClose, onProfile }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token } = useSelector((state) => state.auth);

  const handleLogout = async () => {
    onClose();
    await performLogout(dispatch, token);
    showInfo("You have been signed out.");
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="absolute right-0 top-[calc(100%+6px)] z-50 w-60 rounded-lg border border-primary/10 bg-white p-1 shadow-card"
      role="menu"
    >
      <div className="border-b border-primary/[0.06] px-3 py-2.5">
        <p className="truncate text-sm font-semibold text-primary">{user?.name}</p>
        <p className="truncate text-[11px] text-primary/45">{user?.email}</p>
      </div>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          onProfile();
        }}
        className="mt-0.5 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-primary/70 transition-colors hover:bg-primary/[0.04] hover:text-primary"
      >
        <User className="h-3.5 w-3.5" />
        Profile
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          navigate("/dashboard/settings");
        }}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-primary/70 transition-colors hover:bg-primary/[0.04] hover:text-primary"
      >
        <Settings className="h-3.5 w-3.5" />
        Settings
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={handleLogout}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600/80 transition-colors hover:bg-red-50 hover:text-red-600"
      >
        <LogOut className="h-3.5 w-3.5" />
        Log out
      </button>
    </div>
  );
}

export default function Navbar({
  onMenuClick,
  sidebarCollapsed = false,
  onSidebarToggle,
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const meta = getPageMeta(pathname);
  const user = useSelector((state) => state.auth.user);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuContainerRef = useRef(null);

  const initials = getOpsUserDisplayName(user)
    .split(/\s+/)
    .slice(0, 1)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "U";

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function handlePointerDown(e) {
      if (menuContainerRef.current && !menuContainerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [menuOpen]);

  return (
    <header className="relative z-30 shrink-0 overflow-visible border-b border-primary/[0.08] bg-white">
      <div className="flex h-12 items-center gap-2 px-4 sm:gap-3 sm:px-5">
        <button
          type="button"
          onClick={onMenuClick}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-primary/55 transition-colors hover:bg-primary/[0.04] hover:text-primary lg:hidden"
          aria-label="Open navigation menu"
        >
          <Menu className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={onSidebarToggle}
          className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg text-primary/55 transition-colors hover:bg-primary/[0.04] hover:text-primary lg:inline-flex"
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!sidebarCollapsed}
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>

        <nav
          className="flex min-w-0 shrink-0 items-center gap-1.5 text-xs font-medium sm:text-[13px]"
          aria-label="Breadcrumb"
        >
          <span className="hidden sm:inline">
            <span className="text-accent">Sim</span>
            <span className="text-collect">Collect</span>
            <span className="text-primary/40"> Ops</span>
          </span>
          <span className="hidden text-primary/25 sm:inline">/</span>
          <span className="truncate font-semibold text-primary">{meta.label}</span>
        </nav>

        <div className="ml-3 hidden min-w-0 flex-1 md:block md:max-w-sm lg:max-w-md">
          <GlobalSearch />
        </div>

        <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
          <div className="md:hidden">
            <GlobalSearch mobile />
          </div>

          <NavTasks />

          <div ref={menuContainerRef} className="relative ml-0.5">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="flex items-center gap-2 rounded-lg border border-primary/10 py-1 pl-1 pr-2 transition-colors hover:bg-primary/[0.03]"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-[10px] font-bold text-white">
                {initials}
              </span>
              <ChevronDown
                className={`hidden h-3.5 w-3.5 text-primary/40 sm:block ${menuOpen ? "rotate-180" : ""} transition-transform`}
              />
            </button>
            {menuOpen && (
              <UserMenu
                onClose={() => setMenuOpen(false)}
                onProfile={() => navigate("/dashboard/settings", { state: { section: "profile" } })}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
